import type { SubmissionStatus } from "../types";

/**
 * Format a date for display with weekday, month, and day.
 * Uses browser's locale.
 */
export function formatDateShort(date: Date): string {
  return new Date(date).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

/**
 * Format a date for display with weekday, month, day, and time.
 * Uses browser's locale.
 */
export function formatDateWithTime(date: Date): string {
  return new Date(date).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Format a date as a relative time string (e.g., "Just now", "2h ago", "Yesterday").
 * Falls back to short date format for dates older than 7 days.
 */
export function formatRelativeDate(date: Date | string): string {
  const d = new Date(date);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours === 0) {
      return "Just now";
    }
    return `${diffHours}h ago`;
  }
  if (diffDays === 1) {
    return "Yesterday";
  }
  if (diffDays < 7) {
    return `${diffDays} days ago`;
  }

  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

/**
 * Status badge configuration for form submissions.
 */
export const submissionStatusBadge: Record<
  SubmissionStatus,
  { label: string; class: string }
> = {
  not_started: {
    label: "Not started",
    class: "bg-surface-muted text-text-muted",
  },
  in_progress: {
    label: "In progress",
    class: "bg-amber-100 text-amber-800",
  },
  submitted: {
    label: "Submitted",
    class: "bg-green-100 text-green-800",
  },
};

/**
 * Get a time-based greeting.
 */
export function getTimeBasedGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

/**
 * Format a due date with urgency context.
 * Returns: "Due in 2 days (Fri, Jan 24)" or "Due today!" or "Overdue!"
 *
 * NOTE: This function uses the browser's local timezone for day comparison.
 * For users in different timezones than the workspace, the urgency calculation
 * may be off by a day at timezone boundaries. For accurate results, dates should
 * be normalized to the workspace's timezone before calling this function.
 * TODO: Accept optional timezone parameter and use Intl.DateTimeFormat for
 * timezone-aware date comparison when workspace timezone is available.
 */
export function formatDueDate(date: Date | null): string {
  if (!date) return "";

  const now = new Date();
  const due = new Date(date);
  // Reset time parts for day comparison
  const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());
  const diffMs = dueDay.getTime() - nowDay.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return "Overdue!";
  }
  if (diffDays === 0) {
    return "Due today!";
  }
  if (diffDays === 1) {
    return "Due tomorrow";
  }

  const dayName = due.toLocaleDateString("en-US", { weekday: "short" });
  const monthDay = due.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

  return `Due in ${diffDays} days (${dayName}, ${monthDay})`;
}

/**
 * Shift period_start forward by 1 day for display purposes.
 * DB periods are boundary-to-boundary (e.g. Thu 5pm → Thu 5pm), but the
 * period conceptually covers the day AFTER the boundary through the end.
 * E.g. period_start=Thu Mar 12 → display start = Fri Mar 13.
 */
function displayStart(date: Date): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + 1);
  return d;
}

/**
 * Format a period date range with day names.
 * Returns: "Fri, Mar 13 - Thu, Mar 19, 2026"
 */
export function formatPeriodRange(
  start: Date | null,
  end: Date | null,
): string {
  if (!start) return "";

  const startDate = displayStart(new Date(start));
  const startFmt = startDate.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

  if (!end) return `Starting ${startFmt}`;

  const endDate = new Date(end);
  const endFmt = endDate.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  return `${startFmt} - ${endFmt}`;
}

/**
 * Format a period date range without year, with day names.
 * Returns: "Fri, Mar 13 - Thu, Mar 19"
 */
export function formatPeriodRangeShort(start: Date, end: Date): string {
  const startDate = displayStart(start);
  const endDate = new Date(end);
  const startFmt = startDate.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  const endFmt = endDate.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  return `${startFmt} - ${endFmt}`;
}
